import {
  IonButton,
  IonCard,
  IonCardContent,
  IonDatetime,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonSegment,
  IonSegmentButton,
} from "@ionic/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  LARGE_RANGE_WARN_DAYS,
  MAX_SINCE_HOURS,
  MIN_SINCE_HOURS,
  TimeRangeMode,
  TimeRangeSelection,
  formatRangeSummary,
  getPickerMaxBound,
  normalizePickedDateTime,
  validateCustomRange,
  validateSinceHours,
} from "../utilities/timeRange";
import "./TimeRangeSelector.css";

interface IProps {
  /** Currently applied (fetching) range. Selector edits a draft until Apply. */
  value: TimeRangeSelection;
  /** Called with a validated range the parent should fetch. */
  onApply: (range: TimeRangeSelection) => void;
  /** Placeholder hint, e.g. 24 vs 240 to preserve each page's default. */
  defaultSinceHours: number;
  /** Unique prefix for html ids (page has one instance, but keeps a11y clean). */
  idPrefix: string;
}

const TimeRangeSelector: React.FC<IProps> = ({ value, onApply, defaultSinceHours, idPrefix }) => {
  const [mode, setMode] = useState<TimeRangeMode>(value.mode);
  const [sinceHours, setSinceHours] = useState<number>(value.sinceHours);
  const [startTime, setStartTime] = useState<string>(value.startTime ?? "");
  const [endTime, setEndTime] = useState<string>(value.endTime ?? "");
  const [touchedCustom, setTouchedCustom] = useState(false);

  // Keep draft in sync when parent applies a range (e.g. initial nav state).
  useEffect(() => {
    setMode(value.mode);
    setSinceHours(value.sinceHours);
    setStartTime(value.startTime ?? "");
    setEndTime(value.endTime ?? "");
  }, [value]);

  const pickerMax = useMemo(() => getPickerMaxBound(), []);

  const sinceError = mode === "sinceHours" ? validateSinceHours(sinceHours) : null;
  const customError =
    mode === "custom" && (touchedCustom || startTime || endTime)
      ? validateCustomRange(startTime, endTime)
      : null;

  const showLargeWarning =
    !customError &&
    !sinceError &&
    (mode === "sinceHours"
      ? Number(sinceHours) > LARGE_RANGE_WARN_DAYS * 24
      : !!startTime &&
        !!endTime &&
        (new Date(endTime).getTime() - new Date(startTime).getTime()) / 86400000 >
          LARGE_RANGE_WARN_DAYS);

  const handleModeChange = (next: TimeRangeMode) => {
    setMode(next);
    setTouchedCustom(false);
    if (next === "sinceHours") {
      // Re-validate current input; parent keeps last good value until valid.
      const err = validateSinceHours(sinceHours);
      if (!err) {
        onApply({ mode: "sinceHours", sinceHours: Number(sinceHours) });
      }
    }
  };

  const handleSinceChange = (raw: any) => {
    const parsed = raw === "" || raw === null || raw === undefined ? ("" as any) : Number(raw);
    setSinceHours(parsed);
    if (raw === "" || raw === null || raw === undefined) return;
    if (!validateSinceHours(Number(raw))) {
      // IonInput debounce (1000ms) already throttles; preserves old auto-fetch UX.
      onApply({ mode: "sinceHours", sinceHours: Number(raw) });
    }
  };

  const handleCustomApply = () => {
    setTouchedCustom(true);
    const err = validateCustomRange(startTime, endTime);
    if (err) return;
    onApply({ mode: "custom", sinceHours: Number(sinceHours) || defaultSinceHours, startTime, endTime });
  };

  return (
    <IonCard className="time-range-card">
      <IonCardContent className="ion-no-padding">
        <IonSegment
          value={mode}
          onIonChange={(e) => handleModeChange(e.detail.value as TimeRangeMode)}
          className="time-range-segment"
        >
          <IonSegmentButton value="sinceHours">
            <IonLabel>Last N hours</IonLabel>
          </IonSegmentButton>
          <IonSegmentButton value="custom">
            <IonLabel>Custom range</IonLabel>
          </IonSegmentButton>
        </IonSegment>

        {mode === "sinceHours" ? (
          <IonList lines="none" className="ion-no-padding">
            <IonItem>
              <IonLabel position="stacked">
                Since Hours ({MIN_SINCE_HOURS}-{MAX_SINCE_HOURS} hrs)
              </IonLabel>
              <IonInput
                id={`${idPrefix}-sinceHours`}
                name="sinceHours"
                type="number"
                inputmode="numeric"
                min={String(MIN_SINCE_HOURS)}
                max={String(MAX_SINCE_HOURS)}
                value={sinceHours}
                placeholder={`Default is ${defaultSinceHours} hrs`}
                debounce={1000}
                onIonChange={(e: any) => handleSinceChange(e.detail.value)}
              />
            </IonItem>
            {sinceError ? (
              <div className="errorMsg" role="alert">
                {sinceError}
              </div>
            ) : (
              <IonItem lines="none">
                <IonNote slot="helper" color="medium">
                  Showing: {formatRangeSummary({ mode: "sinceHours", sinceHours: Number(sinceHours) || defaultSinceHours })}
                </IonNote>
              </IonItem>
            )}
          </IonList>
        ) : (
          <IonList lines="none" className="ion-no-padding">
            <IonItem>
              <IonLabel position="stacked">Start (date &amp; time)</IonLabel>
              <IonDatetime
                id={`${idPrefix}-startTime`}
                displayFormat="DD/MM/YYYY HH:mm"
                pickerFormat="DD MMM YYYY HH mm"
                placeholder="Select start"
                max={pickerMax}
                value={startTime || undefined}
                onIonChange={(e) => setStartTime(normalizePickedDateTime(e.detail.value as string))}
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">End (date &amp; time)</IonLabel>
              <IonDatetime
                id={`${idPrefix}-endTime`}
                displayFormat="DD/MM/YYYY HH:mm"
                pickerFormat="DD MMM YYYY HH mm"
                placeholder="Select end"
                max={pickerMax}
                value={endTime || undefined}
                onIonChange={(e) => setEndTime(normalizePickedDateTime(e.detail.value as string))}
              />
            </IonItem>
            {customError ? (
              <div className="errorMsg" role="alert">
                {customError}
              </div>
            ) : (
              startTime &&
              endTime && (
                <IonItem lines="none">
                  <IonNote slot="helper" color="medium">
                    Showing: {formatRangeSummary({ mode: "custom", sinceHours: defaultSinceHours, startTime, endTime })}
                  </IonNote>
                </IonItem>
              )
            )}
            <div className="time-range-apply">
              <IonButton
                expand="block"
                onClick={handleCustomApply}
                disabled={!startTime || !endTime || !!customError}
              >
                Apply range
              </IonButton>
            </div>
          </IonList>
        )}

        {showLargeWarning && (
          <IonItem lines="none">
            <IonNote color="warning" className="ion-text-wrap">
              Range over {LARGE_RANGE_WARN_DAYS} days can be slow or return no data. Narrow the
              range if the graph is empty.
            </IonNote>
          </IonItem>
        )}
      </IonCardContent>
    </IonCard>
  );
};

export default TimeRangeSelector;
